import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { isRouteErrorResponse } from "react-router-dom";
import Translate from "component/global/translation/translation-component";

interface RecordModuleErrorStateProps {
    title?: string;
    description?: string;
    error?: unknown;
    onRetry?: () => void;
}

const DEFAULT_TITLE = "We couldn't load this content";
const DEFAULT_DESCRIPTION =
    "Something unexpected happened while loading this area. You can try again in a moment.";

const getTechnicalErrorMessage = (error: unknown) => {
    if (isRouteErrorResponse(error)) {
        if (typeof error.data === "string" && error.data) {
            return error.data;
        }

        if (typeof error.data?.message === "string" && error.data.message) {
            return error.data.message;
        }

        return error.statusText || `HTTP ${error.status}`;
    }

    const apiError = error as any;
    const backendMessage =
        apiError?.response?.data?.error?.message ||
        apiError?.response?.data?.message;

    if (typeof backendMessage === "string" && backendMessage) {
        return backendMessage;
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === "string" && error) {
        return error;
    }

    try {
        return JSON.stringify(error, null, 2);
    } catch {
        return "No additional technical details were provided.";
    }
};

const RecordModuleErrorState = ({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    error,
    onRetry,
}: RecordModuleErrorStateProps) => {
    const technicalDetails = getTechnicalErrorMessage(error);

    return (
        <Paper
            elevation={1}
            sx={{
                p: 3,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
            }}
        >
            <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <ReportProblemRoundedIcon color="warning" sx={{ mt: 0.25 }} />
                    <Box>
                        <Translate variant="h6" fontWeight={700} text={title} />
                        <Translate variant="body2" color="text.secondary" text={description} />
                    </Box>
                </Stack>

                {onRetry && (
                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<ReplayRoundedIcon />}
                            onClick={onRetry}
                        >
                            <Translate text="Try again" />
                        </Button>
                    </Box>
                )}

                <Accordion disableGutters elevation={0} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                    <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                        <Translate variant="body2" fontWeight={600} text="Technical details" />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography
                            component="pre"
                            variant="body2"
                            sx={{
                                m: 0,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                color: "text.secondary",
                                fontFamily: "monospace",
                            }}
                        >
                            {technicalDetails}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Paper>
    );
};

export default RecordModuleErrorState;
